"use client";

/**
 * EncryptedText — Aceternity-style decrypt reveal.
 *
 * Each character starts as a random gibberish glyph; the characters
 * progressively "lock in" their final value from left to right. Designed
 * to be wrapped around a single word (or short phrase) inside a larger
 * heading. Re-runs the animation whenever its `text` or React `key` changes.
 *
 * Hand-ported (no framer-motion). Same idiom as the existing
 * ScrambleText in components/marketing/animations.tsx, but:
 *   - plays immediately on mount (not on scroll)
 *   - exposes `delay`, `cycleSpeed`, and `revealSpeed` knobs
 *   - keeps width stable by preserving the original text length glyph-for-glyph
 *
 * Paper-warm + brand red. The scrambled glyphs render in a slightly
 * lower-contrast tone via opacity so they read as "noise" not as content.
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const GLYPHS = "!<>-_\\/[]{}—=+*^?#$%&abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type Props = {
  text: string;
  /** Class applied to the final settled text. */
  className?: string;
  /** Class applied to the per-character spans while still scrambling. */
  scrambleClassName?: string;
  /** Total animation duration in ms. Default 900. */
  duration?: number;
  /** Delay before starting. Default 0. */
  delay?: number;
  /** If true, render the final text and skip animation. */
  skip?: boolean;
};

export function EncryptedText({
  text,
  className,
  scrambleClassName,
  duration = 900,
  delay = 0,
  skip = false,
}: Props) {
  // Hydration-safe: initialise with the FINAL text on both server and
  // client. The scramble effect kicks in on mount via useEffect — so
  // SSR and first client render match exactly. (Using Math.random() in
  // the useState initializer would cause React #418 because the server
  // and client roll different glyphs.)
  const [chars, setChars] = useState<string[]>(() => text.split(""));
  const [lockedCount, setLockedCount] = useState<number>(text.length);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (skip || prefersReducedMotion()) {
      setChars(text.split(""));
      setLockedCount(text.length);
      return;
    }

    // Now that we're past hydration, kick off the scramble — replace the
    // settled text with random glyphs, then animate them locking in.
    setChars(text.split("").map((c) => (c === " " ? " " : pickGlyph())));
    setLockedCount(0);

    let startedAt = 0;
    let timeoutId: number | undefined;

    const tick = (t: number) => {
      if (!startedAt) startedAt = t;
      const elapsed = t - startedAt;
      const p = Math.min(1, elapsed / duration);
      const lockUntil = Math.floor(p * text.length);

      setChars((prev) => {
        const next = prev.slice();
        for (let i = 0; i < text.length; i++) {
          if (i < lockUntil) {
            next[i] = text[i];
          } else if (text[i] === " ") {
            next[i] = " ";
          } else {
            next[i] = pickGlyph();
          }
        }
        return next;
      });
      setLockedCount(lockUntil);

      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setChars(text.split(""));
        setLockedCount(text.length);
      }
    };

    timeoutId = window.setTimeout(() => {
      rafRef.current = requestAnimationFrame(tick);
    }, delay);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      cancelAnimationFrame(rafRef.current);
    };
  }, [text, duration, delay, skip]);

  return (
    <span className={cn("inline", className)}>
      {/* The only text copy — see LetterReveal. The glyphs are drawn from `data-ch` by CSS, so
          search engines and the clipboard see the headline once, not twice. */}
      <span className="sr-only">{text}</span>
      {chars.map((c, i) => {
        const locked = i < lockedCount;
        if (c === " ") return <span key={i} aria-hidden>{" "}</span>;
        return (
          <span
            key={i}
            aria-hidden
            data-ch={c}
            className={cn(
              "inline-block glyph-ch transition-[opacity,color] duration-100",
              locked ? null : cn("opacity-55", scrambleClassName)
            )}
          />
        );
      })}
    </span>
  );
}

function pickGlyph(): string {
  return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
}
